import React, { useEffect } from 'react';

const warn = (content: string) =>
  console.warn('[react-screen-wake-lock]: ' + content);

export interface WakeLockOptions {
  onError?: (error: Error) => void;
  onRequest?: () => void;
  onRelease?: EventListener;
}

// https://caniuse.com/mdn-api_wakelock
const isSupported = typeof window !== 'undefined' && 'wakeLock' in navigator;

interface UseWakeLockResponse {
  isSupported: boolean;
  released?: boolean;
  type?: WakeLockType;
  request: () => Promise<void>;
  release: () => Promise<void>;
}

export const useWakeLock = ({
  onError,
  onRequest,
  onRelease,
}: WakeLockOptions | undefined = {}): UseWakeLockResponse => {
  const [released, setReleased] = React.useState<boolean | undefined>();
  const wakeLock = React.useRef<WakeLockSentinel | null>(null);

  const request = React.useCallback(
    async (type: WakeLockType = 'screen') => {
      const isWakeLockAlreadyDefined = wakeLock.current != null;
      if (!isSupported) {
        return warn(
          "Calling the `request` function has no effect, Wake Lock Screen API isn't supported"
        );
      }
      if (isWakeLockAlreadyDefined) {
        return warn(
          'Calling `request` multiple times without `release` has no effect'
        );
      }

      try {
        wakeLock.current = await navigator.wakeLock.request(type);

        wakeLock.current.onrelease = (e: Event) => {
          // Default to `true` - `released` API is experimental: https://caniuse.com/mdn-api_wakelocksentinel_released
          setReleased((wakeLock.current && wakeLock.current.released) || true);
          onRelease?.(e);
          wakeLock.current = null;
        };

        onRequest?.();
        setReleased(wakeLock.current?.released ?? false);
      } catch (error: any) {
        onError?.(error);
      }
    },
    [onRequest, onError, onRelease]
  );

  const release = React.useCallback(async () => {
    const isWakeLockUndefined = wakeLock.current == null;
    if (!isSupported) {
      return warn(
        "Calling the `release` function has no effect, Wake Lock Screen API isn't supported"
      );
    }

    if (isWakeLockUndefined) {
      return warn('Calling `release` before `request` has no effect.');
    }

    await wakeLock.current?.release();
  }, []);

  useEffect(() => {
    return () => {
      if (isSupported) {
        release();
      }
    };
  }, [release]);

  return {
    isSupported,
    request,
    released,
    release,
    type: wakeLock.current?.type,
  };
};
